/**
 * Core colours.
 *
 * "Her karakterin kendine ait benzersiz bir rengi vardır" is a gameplay rule, not
 * decoration: you grow by collecting energy of *your* colour and score by destroying
 * everyone else's. Two players who look alike cannot tell their ammunition apart.
 *
 * The palette holds exactly one colour per seat in a full room. It was chosen by
 * farthest-point sampling in CIELAB over vivid candidates, with two constraints
 * beyond mutual separation: every entry stays clear of the energy-station yellow
 * (#ffd84a) and reads against the deep-navy arena. Minimum pairwise ΔLab is ~30,
 * which `tests/game-palette.test.mjs` enforces — adding a colour by hand without
 * re-running that check will quietly break the rule above.
 */

export const ENERGY_STATION_YELLOW = "#ffd84a";

export const CORE_COLORS = [
  "#1aff1a",
  "#0000f0",
  "#f00000",
  "#1ad1ff",
  "#f971cc",
  "#f9be71",
  "#1a7dff",
  "#71f9b1",
  "#f7ff1a",
  "#f71aff",
  "#f97171",
  "#ff851a",
  "#baf971",
  "#ff1a7d",
  "#ba71f9",
  "#1afff7",
  "#1aff7d",
  "#71a7f9",
  "#0048f0",
  "#abff1a",
] as const;

export const CORE_NAMES = [
  "LUNA", "ORBIT", "ZED", "PIXEL", "VOLT",
  "ION", "SOL", "RIFT", "MIRA", "ECHO",
  "EMBER", "KAI", "NYX", "AERO", "HELIOS",
  "VEX", "PYRO", "GAIA", "QUARK", "ASTRA",
] as const;

/**
 * Hands out the first colour nobody in the room holds. Falls back to the least
 * recently reused entry if a room ever exceeds the palette, so two cores can still
 * be told apart by name even in that degenerate case.
 */
export function allocateColor(taken: Iterable<string>): string {
  const used = new Set<string>();
  for (const color of taken) used.add(color.toLowerCase());
  for (const color of CORE_COLORS) {
    if (!used.has(color)) return color;
  }
  return CORE_COLORS[used.size % CORE_COLORS.length];
}

/** Deterministic colour for a bot seat, avoiding anything already in the room. */
export function allocateBotColor(index: number, taken: Iterable<string>): string {
  const used = new Set<string>();
  for (const color of taken) used.add(color.toLowerCase());
  for (let step = 0; step < CORE_COLORS.length; step += 1) {
    const candidate = CORE_COLORS[(index + step) % CORE_COLORS.length];
    if (!used.has(candidate)) return candidate;
  }
  return CORE_COLORS[index % CORE_COLORS.length];
}
